/**
 * Strictness copy for the Project settings dialog (Story 514).
 *
 * This is text only a person reads, so it lives here, in the app, and not in
 * the prompts table: that table holds agent-facing text (tool descriptions,
 * tool replies, guidance an MCP client reads). The one sentence on what a
 * level does is shared with the manage tool's confirmation, so it stays a
 * prompt and arrives through the settings read route as `consequence`.
 */

export type Strictness = 'lenient' | 'standard' | 'strict';

export const STRICTNESS_LABELS: Record<Strictness, string> = {
  lenient: 'Lenient',
  standard: 'Standard',
  strict: 'Strict',
};

/** When to use each level and when not to. Shown beside the radio for that level. */
export const STRICTNESS_GUIDANCE: Record<Strictness, readonly string[]> = {
  lenient: [
    'The default. Use for existing codebases, exploration, onboarding a team, or any project where working outside Ceetrix is normal.',
    'Nothing is refused or instructed when no task is bound; unstoried work is recorded so a later sync can offer to link it.',
    'Do not use when unbound work must be blocked.',
  ],
  standard: [
    'Use when most work is storied but chores and repairs are common.',
    'The agent is told when nothing is bound and may ask you whether to bind a task or carry on.',
    'Do not use for unattended runs, where asking stalls the work.',
  ],
  strict: [
    'Use for high-assurance or regulated work, or a project built entirely through Ceetrix from the start.',
    'Writes with no task bound are refused with a reason the agent reads.',
    "Do not use on repositories with heavy unstoried maintenance. It still depends on the coding tool loading Ceetrix's configuration, which a developer can remove.",
  ],
};

// ---------------------------------------------------------------------------
// Story 537, widened by story 545: which validation service answers the content gates.
// Web copy: only a person reads it. Each choice names its vendor, so who
// receives the content is visible on the choice itself.
// ---------------------------------------------------------------------------

export type ContentValidationService = 'gemini' | 'typesafe' | 'fireworks';

/** The services that could be offered again; Gemini is no longer used (2026-10-15) and has no copy. */
export type OfferableService = Exclude<ContentValidationService, 'gemini'>;

export const SERVICE_LABELS: Record<OfferableService, string> = {
  typesafe: 'TypeSafe',
  fireworks: 'DeepSeek on Fireworks',
};

/** One line under each choice, so the vendor is visible before anything is sent. */
export const SERVICE_DESCRIPTIONS: Record<OfferableService, string> = {
  typesafe: 'Assessed by TypeSafe AI against fixed criteria, in the United States.',
  fireworks: 'Assessed by DeepSeek V4.1-Flash running at Fireworks AI, in the United States.',
};

/** Task 537.43: shown beneath the TypeSafe choice, per gate, from the thresholds in force. */
export const TYPESAFE_DISABLED_INTRO = 'Not enforced at the current thresholds:';
export const TYPESAFE_GATE_LABELS = {
  prd: 'PRD',
  design: 'design',
  evidence: 'evidence',
  'story-title': 'story title',
} as const;
export const TYPESAFE_NOTHING_DISABLED = 'none';
export const TYPESAFE_DISABLED_UNREADABLE = 'could not be read';
/** Rule wordings can contain commas, so they are separated by semicolons. */
export const TYPESAFE_RULE_SEPARATOR = '; ';

export const SERVICE_GROUP_LABEL = 'Content validation service';

/** Shown above the choices, so the scope of the setting is stated once. */
export const SERVICE_GROUP_DESCRIPTION =
  'Which service validates this project\'s PRDs, designs, task completion evidence and story titles. It changes nothing else.';

export const SERVICE_NOT_SAVED_MESSAGE = 'The validation service was not changed. Try again.';

/** Story 540: the footer's single control. Nothing is pending, so it finishes rather than saves. */
export const DONE_LABEL = 'Done';

/** Story 540: confirms which level is in force, after a choice has been applied. */
export function strictnessAppliedMessage(level: Strictness): string {
  return `Now set to ${STRICTNESS_LABELS[level]}. This applies to every story in the project.`;
}
export const SERVICE_CONFLICT_MESSAGE = 'The validation service was changed by someone else since you opened this. The switch now shows the current value.';


// ---------------------------------------------------------------------------
// Story 556: the one setting the dialog offers, whether finishing a task needs
// a commit. Web copy: only a person reads it. The two benefit lines are the
// operator's reasons (2026-09-28): who changed what, and agents that cannot
// claim code changes they did not make.
// ---------------------------------------------------------------------------

export const COMMIT_LEGEND = 'Require a commit when a task is finished';
export const COMMIT_BENEFIT_WHO = 'Easier to see who changed what: every finished task names the commit it was finished at.';
export const COMMIT_BENEFIT_HONEST = 'Makes it harder for a coding agent to claim code changes it did not make.';
export const COMMIT_APPLIED_ON = 'Now on. Finishing a task in this project needs a commit.';
export const COMMIT_APPLIED_OFF = 'Now off. A task can be finished without a commit; a commit that is given is still recorded.';
export const COMMIT_OWNER_ONLY = 'The project owner can change this.';
export const COMMIT_SAVE_FAILED_MESSAGE = 'The change was not applied. Try again.';
export const COMMIT_CONFLICT_MESSAGE = 'Changed by someone else since you opened this. The switch now shows the setting in force; change it again to replace it.';
export const COMMIT_LOAD_FAILED_MESSAGE = 'Project settings could not be loaded.';

// ---------------------------------------------------------------------------
// Story 564: project settings as a page, with a section list. Web copy: only a
// person reads it.
// ---------------------------------------------------------------------------

export const SETTINGS_TITLE = 'Project settings';
export const SETTINGS_BACK_LABEL = 'Back to workspace';
export const SETTINGS_OWNER_ONLY_NOTE = 'Only the project owner can change these settings.';
/** Names the chooser that replaces the section list on narrow screens. */
export const SETTINGS_SECTION_CHOOSER_LABEL = 'Section';
export const SETTINGS_LOADING_MESSAGE = 'Loading project settings…';
export const SECTION_LABEL_TASK_COMPLETION = 'Task completion';

// Story 546: AI Services (layout approved 2026-10-12). Only a person reads these.
export const SECTION_LABEL_AI_SERVICES = 'AI Services';
export const AI_SERVICE_CHOICE_LEGEND = "Who runs this project's AI Service checks";
export const AI_SERVICE_CHOICE_CEETRIX = 'Ceetrix';
export const AI_SERVICE_CHOICE_OWN = 'I choose the service';
export const AI_SERVICE_CEETRIX_NOTE = "Checks run on Ceetrix's account.";
export const AI_SERVICE_NOT_YET_NOTE = "Checks stay on Ceetrix's account until the service you enter passes its check.";
export const AI_SERVICE_ADDRESS_LABEL = 'Address';
export const AI_SERVICE_ADDRESS_DEFAULT = 'https://api.anthropic.com';
export const AI_SERVICE_ADDRESS_HINT = "Anthropic's, or any Anthropic- or OpenAI-compatible address, including your own gateway.";
export const AI_SERVICE_KEY_LABEL = 'Key';
export const AI_SERVICE_KEY_SAVED_PLACEHOLDER = (last4: string) => `Saved key ending ${last4}. Type a new key to replace it.`;
export const AI_SERVICE_KEY_GUIDANCE: readonly string[] = [
  'Give Ceetrix a key used only by Ceetrix, with its own spending limit at your provider. A leaked key can then spend no more than that limit.',
  "Or enter your own gateway's address and a token for it, so your provider key never reaches Ceetrix.",
  'Ceetrix stores the key so a copy of its data does not reveal it. Ceetrix itself can use it, because it runs your checks while you are away.',
];
export const AI_SERVICE_MODEL_LABEL = 'Model';
export const AI_SERVICE_EFFORT_LABEL = 'Effort';
export const AI_SERVICE_EFFORT_DEFAULT = 'Provider default';
export const AI_SERVICE_EFFORT_LABELS = { low: 'Low', medium: 'Medium', high: 'High' } as const;
export const AI_SERVICE_FORMAT_LABELS = { anthropic: 'Anthropic-compatible', openai: 'OpenAI-compatible' } as const;
export const AI_SERVICE_TEST_LABEL = 'Test';
export const AI_SERVICE_CHECKING_LABEL = 'Checking…';
export const AI_SERVICE_WORKS = 'Works';
export const AI_SERVICE_PAUSED = (reason: string, nextTry: string) => `Paused: ${reason}. Next try ${nextTry}.`;
/** Story 546, task 546.29: what each recorded check kind is called on screen. */
export const AI_SERVICE_CHECK_NAMES: Record<string, string> = {
  prd: 'PRD',
  design: 'design',
  'story-title': 'story title',
  evidence: 'task evidence',
  diff_qa: 'code change check',
  g9_extraction: 'test plan',
  g10_body: 'task text',
  design_coverage: 'design coverage',
};
/** A recorded ref with a dot is a task (199.3); otherwise it is a story. */
export const AI_SERVICE_CHECK_SUBJECT = (check: string, ref: string) => {
  const name = AI_SERVICE_CHECK_NAMES[check] ?? check;
  if (!ref) return name;
  return ref.includes('.') ? `${name} for task ${ref}` : `${name} for story ${ref}`;
};
export const AI_SERVICE_LAST_CHECK_FAILURE = (subject: string, when: string, words: string) =>
  `Last check this model couldn't take: ${subject}, ${when}. The provider said: ${words}`;
export const AI_SERVICE_STATE_MESSAGES = {
  customer_fix: 'AI Service checks are off: fix this with the provider. Checks resume by themselves once it is fixed.',
  stopped: 'AI Service checks are off: fix this with the provider, then press Test; or enter another key.',
  unusable: 'AI Service checks are off: Ceetrix could not open the saved key. Enter the key again.',
} as const;
export const AI_SERVICE_REQUEST_FAILED = 'The request did not reach Ceetrix. Nothing was changed; try again.';
export const AI_SERVICE_ALERT_TITLE = 'AI Service checks are off for this project.';
export const AI_SERVICE_ALERT_LINK = 'Open AI Services';
export const AI_SERVICE_LAST_WORKED = (when: string) => `Last worked ${when}.`;
